import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import { applyGroupedDataPage, applyGroupFetchRejected } from 'src/redux/common/reducers';
import {
  addResidentAvailability,
  bulkRequestResidentAvailability,
  getServiceRequestDetails,
  getServiceRequests,
  getServiceRequestsForGroup,
  rejectServiceRequest,
  requestResidentAvailability,
  updateResidentAvailability,
  updateServiceRequestDescription,
  updateServiceRequestDetails,
  updateServiceRequestPriority,
  updateServiceRequestResidentInfo,
} from 'src/redux/service-request/api';
import type {
  InitialStateType,
  ResidentAvailabilityResponseType,
  ServiceRequestAPIResponseObj,
  ServiceRequestDataObj,
} from 'src/redux/service-request/types';
import type { Issue } from '../issues/types';

const extraReducers = (builder: ActionReducerMapBuilder<InitialStateType>) => {
  /**
   * Service request
   */
  builder.addCase(getServiceRequests().pending, (state: InitialStateType, action) => {
    if (!action.meta.arg.service_request_id) {
      state.isLoading = true;
      state.currentFilterReferenceObj = action.meta.arg;
    }
  });
  builder.addCase(getServiceRequests().fulfilled, (state: InitialStateType, action) => {
    const searchQuery = new URLSearchParams(window?.location?.search);
    const expandedServiceRequestId =
      searchQuery?.get('srId') || state?.showSidePanel?.serviceRequestUuid || '';
    const newSource = { ...(state?.source || {}) };
    const newWoIds = [...(Number(action.meta.arg.page || 0) > 1 ? state?.data?.data || [] : [])];
    action?.payload?.data?.forEach((item: ServiceRequestAPIResponseObj) => {
      // checking the new sr id is already exist in previous list.
      if (item?.service_request_id && newWoIds?.includes(item?.service_request_id)) {
        newWoIds.splice(newWoIds.indexOf(item?.service_request_id), 1);
      }
      newWoIds.push(item?.service_request_id);
      if (
        item?.service_request_id !== expandedServiceRequestId ||
        isEmpty(state?.source?.[expandedServiceRequestId])
      ) {
        newSource[item?.service_request_id] = item;
      }
    });
    if (!action.meta.arg.service_request_id) {
      state.data = {
        data: newWoIds,
        meta: action?.payload?.meta || {},
      };
      state.isLoading = false;
    }
    state.source = newSource;
  });
  builder.addCase(getServiceRequests().rejected, (state: InitialStateType, action) => {
    if (!action.meta.arg.service_request_id) {
      state.isLoading = false;
    }
  });
  // Fetch Service request details
  builder.addCase(getServiceRequestDetails().pending, (state: InitialStateType) => {
    // Any new attempt (initial load or a Retry) clears a prior failure.
    state.hasServiceRequestDetailsError = false;
    if (!state?.showSidePanel?.needToRefetch) {
      state.isFetchingServiceRequestDetails = true;
    }
  });
  builder.addCase(getServiceRequestDetails().fulfilled, (state: InitialStateType, action) => {
    const currentData = state.source[action?.payload?.service_request_id];
    const isFieldsNotEditable = action?.payload?.is_scheduled_for_lula;

    state.source[action?.payload?.service_request_id] = {
      ...currentData,
      ...action?.payload,
      added_date: action?.payload?.date_added,
      resident_availabilities: {
        ...(action?.payload?.resident_availabilities || {}),
        availability: action?.payload?.resident_availabilities?.availability,
      },
      issues: Array.isArray(action?.payload?.issues)
        ? action?.payload?.issues?.map((issue: Issue) => issue?.issue_id)
        : [],
      nonEditableFields: isFieldsNotEditable
        ? [
            'address',
            'resident_info',
            'nte',
            'media',
            'issues',
            'resident_availability',
            'access_info',
          ]
        : [],
      issuesData: Array.isArray(action?.payload?.issues) ? action?.payload?.issues : [],
    };

    const availableDates =
      [] as ServiceRequestDataObj['resident_availabilities']['available_dates'];

    const residentAvailabilitySource =
      {} as ServiceRequestDataObj['resident_availabilities']['resident_availability_source'];

    action?.payload?.resident_availabilities?.availability?.forEach(
      (availability: ResidentAvailabilityResponseType) => {
        if (availability.date) {
          availableDates?.push(availability.date);
          if (residentAvailabilitySource) {
            residentAvailabilitySource[availability.date] = availability;
          }
        }
      },
    );

    state.source[action?.payload?.service_request_id].resident_availabilities.available_dates =
      availableDates;
    state.source[
      action?.payload?.service_request_id
    ].resident_availabilities.resident_availability_source = residentAvailabilitySource;

    // Server truth has replaced `resident_availabilities` wholesale above, so there is
    // no optimistic stamp left to settle or roll back (APX-607).
    if (state.pendingAvailabilityRequests?.[action?.payload?.service_request_id]) {
      delete state.pendingAvailabilityRequests[action?.payload?.service_request_id];
    }

    state.isFetchingServiceRequestDetails = false;
    state.hasServiceRequestDetailsError = false;
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: false,
    };
  });
  builder.addCase(getServiceRequestDetails().rejected, (state: InitialStateType, action) => {
    state.isFetchingServiceRequestDetails = false;
    // Only a genuine load failure lights the error state — a canceled/aborted request (SR
    // switch, unmount) or the 401 sign-out shape must not surface as "couldn't load".
    const actionType = (action.payload as { actionType?: string } | undefined)?.actionType;
    state.hasServiceRequestDetailsError =
      actionType !== 'canceled' && actionType !== 'unauthorized';
    state.showSidePanel = {
      ...(state.showSidePanel || {}),
      needToRefetch: false,
    };
  });
  // update service request detials
  builder.addCase(updateServiceRequestDetails().pending, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = true;
  });
  builder.addCase(updateServiceRequestDetails().fulfilled, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  builder.addCase(updateServiceRequestDetails().rejected, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  // update service request priority
  builder.addCase(updateServiceRequestPriority().pending, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = true;
  });
  builder.addCase(updateServiceRequestPriority().fulfilled, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  builder.addCase(updateServiceRequestPriority().rejected, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  // update service request resident info
  builder.addCase(updateServiceRequestResidentInfo().pending, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = true;
  });
  builder.addCase(updateServiceRequestResidentInfo().fulfilled, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  builder.addCase(updateServiceRequestResidentInfo().rejected, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  // update service request description
  builder.addCase(updateServiceRequestDescription().pending, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = true;
  });
  builder.addCase(updateServiceRequestDescription().fulfilled, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });
  builder.addCase(updateServiceRequestDescription().rejected, (state: InitialStateType) => {
    state.isUpdatingServiceRequestDetails = false;
  });

  builder.addCase(addResidentAvailability().pending, (state: InitialStateType) => {
    state.isAddingResidentAvailability = true;
  });
  builder.addCase(addResidentAvailability().fulfilled, (state: InitialStateType) => {
    state.isAddingResidentAvailability = false;
    state.isManualAddingResidentAvailability = false;
  });
  builder.addCase(addResidentAvailability().rejected, (state: InitialStateType) => {
    state.isAddingResidentAvailability = false;
  });

  builder.addCase(updateResidentAvailability().pending, (state: InitialStateType) => {
    state.isUpdatingResidentAvailability = true;
  });
  builder.addCase(updateResidentAvailability().fulfilled, (state: InitialStateType) => {
    state.isUpdatingResidentAvailability = false;
  });
  builder.addCase(updateResidentAvailability().rejected, (state: InitialStateType) => {
    state.isUpdatingResidentAvailability = false;
  });

  builder.addCase(requestResidentAvailability().pending, (state: InitialStateType) => {
    state.isRequestingResidentAvailability = true;
  });
  builder.addCase(requestResidentAvailability().fulfilled, (state: InitialStateType) => {
    state.isRequestingResidentAvailability = false;
  });
  builder.addCase(requestResidentAvailability().rejected, (state: InitialStateType) => {
    state.isRequestingResidentAvailability = false;
  });

  // Reject service request
  builder.addCase(rejectServiceRequest().pending, (state: InitialStateType) => {
    state.isRejectingServiceRequest = true;
  });
  builder.addCase(rejectServiceRequest().fulfilled, (state: InitialStateType, action) => {
    state.isRejectingServiceRequest = false;
    const { serviceRequestId, ...rejectionData } = action?.payload || {};
    if (serviceRequestId && state.source[serviceRequestId]) {
      const current = state.source[serviceRequestId];
      state.source[serviceRequestId] = {
        ...current,
        status: rejectionData?.status ?? current.status,
        rejection: rejectionData?.rejection ?? current.rejection,
        // RejectedResource returns the ability set recomputed AFTER the transition commits, so
        // prefer it: it has already cleared every ability the rejection revoked (create_work_order,
        // create_issue, request_availability, closed), which a hand-rolled filter would leave stale.
        // The fallback is load-bearing, not dead code: the resource omits `abilities` entirely for
        // device requests (`$this->when(! expectsDevice(), ...)`), so keep dropping the one ability
        // this flow is known to revoke when the key is absent. Either way the button hides
        // immediately (flash-avoidance) before the SR-details refetch re-syncs the full set.
        abilities:
          (rejectionData?.abilities as string[] | undefined) ??
          (current.abilities ?? []).filter((ability) => ability !== 'create_work_order'),
      };
    }
  });
  builder.addCase(rejectServiceRequest().rejected, (state: InitialStateType) => {
    state.isRejectingServiceRequest = false;
  });

  builder.addCase(bulkRequestResidentAvailability().pending, () => {});
  builder.addCase(bulkRequestResidentAvailability().fulfilled, () => {});
  builder.addCase(bulkRequestResidentAvailability().rejected, () => {});

  builder.addCase(getServiceRequestsForGroup().fulfilled, (state: InitialStateType, action) => {
    const groupKey = action.payload?._group_key as string | undefined;
    if (!groupKey) return;
    const items: ServiceRequestAPIResponseObj[] = action.payload?.data ?? [];
    const meta = action.payload?.meta;

    // Mirror the flat listing's guard: don't clobber the currently-expanded SR's
    // richer detail-panel data with the lighter listing payload.
    const searchQuery = new URLSearchParams(window?.location?.search);
    const expandedServiceRequestId =
      searchQuery?.get('srId') || state?.showSidePanel?.serviceRequestUuid || '';

    items.forEach((serviceRequest) => {
      if (
        serviceRequest.service_request_id !== expandedServiceRequestId ||
        isEmpty(state?.source?.[expandedServiceRequestId])
      ) {
        state.source[serviceRequest.service_request_id] = serviceRequest as ServiceRequestDataObj;
      }
    });

    const newIds = items.map((serviceRequest) => serviceRequest.service_request_id);
    applyGroupedDataPage(
      state,
      groupKey,
      newIds,
      meta,
      action.payload?._attempt as number | undefined,
    );
  });

  builder.addCase(getServiceRequestsForGroup().rejected, (state: InitialStateType, action) => {
    applyGroupFetchRejected(state, action);
  });
};
export default extraReducers;
